import { getSupabaseBaseUrl, supabase } from "@/services/supabaseClient";
import { getCurrentPatientId } from "@/services/appointmentService.js";
import {
  isPastAppointment,
  isPendingApprovalAppointment,
  isUpcomingAppointment,
  normalizeAppointmentStatus,
} from "@/services/appointmentLifecycle.js";
import { isFavoriteDoctor } from "@/services/doctorFavorites.js";
import { resolvePatientFacingDoctorName } from "@/utils/doctorPatientFacingName.js";
import { pickDiagnosisNotes, pickDiagnosisTitle } from "@/utils/diagnosis.js";
import { normalizeVerificationStatus } from "@/utils/verificationStatus.js";

function toDateISO(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function toHHMM(value) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function plusMinutesHHMM(timeHHMM, minutesToAdd) {
  const match = String(timeHHMM || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return "";
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";
  const total = hours * 60 + minutes + Number(minutesToAdd || 0);
  const normalized = ((total % 1440) + 1440) % 1440;
  const nextHours = String(Math.floor(normalized / 60)).padStart(2, "0");
  const nextMinutes = String(normalized % 60).padStart(2, "0");
  return `${nextHours}:${nextMinutes}`;
}

function shouldAppearInPastAppointmentsList(row, nowMs = Date.now()) {
  if (row?.superseded_by_appointment_id) return false;

  // Appointment history is a clinical record, not an elapsed-time bucket. A
  // pending, cancelled, rescheduled, current, or merely elapsed appointment
  // must never appear until the consultation is genuinely completed.
  if (normalizeAppointmentStatus(row?.status) !== "completed") return false;

  const startMs = row?.appointment_date ? new Date(row.appointment_date).getTime() : NaN;
  if (!Number.isFinite(startMs)) return false;

  const durationMinutes = Math.max(0, Number(row?.duration_minutes || 0));
  return startMs + durationMinutes * 60 * 1000 <= nowMs;
}

// Completion records clinician close-out. They do not, on their own, prove
// that the patient entered a consultation. Reviews therefore need a recorded
// patient video admission.
export function hasPatientConsultationParticipation(snapshot = {}) {
  const mode = String(snapshot?.mode || snapshot?.consultation_type || "").trim().toLowerCase();
  return (
    (mode === "evisit" || mode === "virtual" || mode === "private") &&
    Boolean(String(snapshot?.patientJoinedAt || snapshot?.patient_joined_at || "").trim())
  );
}

async function loadPatientParticipationByAppointmentIds(appointmentIds = []) {
  const ids = Array.from(
    new Set((Array.isArray(appointmentIds) ? appointmentIds : []).map((id) => String(id || "").trim()).filter(Boolean)),
  );
  if (ids.length === 0) return new Map();

  try {
    const { data, error } = await supabase
      .from("appointment_video_sessions")
      .select("appointment_id, patient_joined_at")
      .in("appointment_id", ids);
    if (error) return new Map();
    return new Map(
      (Array.isArray(data) ? data : []).map((row) => [
        String(row?.appointment_id || "").trim(),
        String(row?.patient_joined_at || "").trim(),
      ]),
    );
  } catch {
    return new Map();
  }
}

function isGenericServiceName(value) {
  return /^(consult(?:ation|ancy)|first time consultancy)$/i.test(
    String(value || "").trim()
  );
}

function formatAmount(value, currency = "ZAR") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  return `${amount.toFixed(2)} ${String(currency || "ZAR").trim() || "ZAR"}`;
}

function prettyPaymentMethod(value, fallback = "Debit / Credit cards") {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return fallback;

  const aliases = {
    card: "Debit / Credit cards",
    credit: "Debit / Credit cards",
    debit: "Debit / Credit cards",
    visa: "Debit / Credit cards",
    mastercard: "Debit / Credit cards",
    amex: "Debit / Credit cards",
    bank: "Bank transfers",
    transfer: "Bank transfers",
    eft: "Instant EFT",
    cash: "Cash on service",
    wallet: "E-wallets",
  };

  const direct = aliases[raw];
  if (direct) return direct;

  if (raw.includes("wallet")) return "E-wallets";
  if (raw.includes("bank") || raw.includes("transfer")) return "Bank transfers";
  if (raw.includes("eft")) return "Instant EFT";
  if (raw.includes("cash")) return "Cash on service";
  if (
    raw.includes("card") ||
    raw.includes("visa") ||
    raw.includes("mastercard") ||
    raw.includes("amex")
  ) {
    return "Debit / Credit cards";
  }

  return fallback;
}

function normalizePaymentLedgerStatus(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (raw === "pending") return "pending";
  if (raw === "succeeded" || raw === "paid") return "paid";
  if (raw === "refunded") return "refunded";
  if (raw === "refund_pending") return "refund_pending";
  if (raw === "refund_failed") return "refund_failed";
  if (raw === "cancelled" || raw === "canceled") return "cancelled";
  return raw;
}

function safeJSONParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function hasStrongDoctorText(value, fallback = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text === String(fallback || "").trim()) return false;
  return true;
}

function readStoredDoctorCache(doctorId) {
  if (typeof window === "undefined") return null;
  const id = String(doctorId || "").trim();
  const keys = id ? [`afrimed:doctor:${id}`, "afrimed:lastDoctor"] : ["afrimed:lastDoctor"];

  for (const key of keys) {
    try {
      const raw = sessionStorage.getItem(key);
      const parsed = raw ? safeJSONParse(raw) : null;
      if (!parsed) continue;
      if (!id || String(parsed?.id || "").trim() === id) return parsed;
    } catch {
      // ignore
    }
    try {
      const raw = localStorage.getItem(key);
      const parsed = raw ? safeJSONParse(raw) : null;
      if (!parsed) continue;
      if (!id || String(parsed?.id || "").trim() === id) return parsed;
    } catch {
      // ignore
    }
  }

  return null;
}

function normalizeSpecialty(value) {
  if (Array.isArray(value)) {
    const first = value.find((entry) => String(entry || "").trim());
    return first ? String(first).trim() : "Doctor";
  }
  return String(value || "").trim() || "Doctor";
}

function normalizeLanguages(value) {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || "").trim()).filter(Boolean).join(", ");
  }
  return String(value || "").trim();
}

function resolveDoctorAvatar(primaryUrl, photoPath, fallbackUrl = "") {
  const direct = String(primaryUrl || "").trim();
  if (direct) return direct;

  const path = String(photoPath || "").trim();
  if (path) {
    if (/^https?:\/\//i.test(path)) return path;
    const base = getSupabaseBaseUrl();
    const cleanPath = path.replace(/^public\//, "");
    if (base && cleanPath) {
      return `${base}/storage/v1/object/public/doctor-profile-images/${cleanPath}`;
    }
  }

  const fallback = String(fallbackUrl || "").trim();
  return fallback || "";
}

function normalizeMode(consultationType, fallbackMode = null, fallbackFacility = null) {
  const explicit = String(fallbackMode || "").trim().toLowerCase();
  if (explicit === "evisit" || explicit === "inperson") return explicit;

  const raw = String(consultationType || "").trim().toLowerCase();
  if (raw === "private" || raw === "telehealth" || raw === "virtual") return "evisit";
  if (fallbackFacility) return "inperson";
  return "inperson";
}

function resolveFacility(appointmentRow, consultanciesById, firstConsultancyByDoctor, legacyByDoctor, fallback) {
  const practiceId = String(appointmentRow?.practice_id || "").trim();
  const doctorId = String(appointmentRow?.doctor_id || "").trim();

  const consultancy =
    (practiceId && consultanciesById.get(practiceId)) ||
    firstConsultancyByDoctor.get(doctorId) ||
    null;

  if (consultancy) {
    return {
      id: consultancy?.id || appointmentRow?.practice_id || null,
      name: consultancy?.facility_name || fallback?.facilityName || fallback?.facility?.name || "",
      address: consultancy?.facility_address || fallback?.facility?.address || "",
      city: consultancy?.facility_city || fallback?.facility?.city || "",
      country: consultancy?.facility_country || fallback?.facility?.country || "",
      lat:
        consultancy?.facility_lat ??
        consultancy?.facility_latitude ??
        consultancy?.lat ??
        consultancy?.latitude ??
        fallback?.facility?.lat ??
        fallback?.facility?.latitude ??
        null,
      lng:
        consultancy?.facility_lng ??
        consultancy?.facility_longitude ??
        consultancy?.lng ??
        consultancy?.lon ??
        consultancy?.longitude ??
        fallback?.facility?.lng ??
        fallback?.facility?.longitude ??
        null,
    };
  }

  const legacy = legacyByDoctor.get(doctorId) || null;
  if (legacy) {
    return {
      id: appointmentRow?.practice_id || legacy?.id || null,
      name: legacy?.facility_name || fallback?.facilityName || fallback?.facility?.name || "",
      address: legacy?.street_address || fallback?.facility?.address || "",
      city: legacy?.city || fallback?.facility?.city || "",
      country: legacy?.country || fallback?.facility?.country || "",
      lat:
        legacy?.facility_lat ??
        legacy?.facility_latitude ??
        legacy?.lat ??
        legacy?.latitude ??
        fallback?.facility?.lat ??
        fallback?.facility?.latitude ??
        null,
      lng:
        legacy?.facility_lng ??
        legacy?.facility_longitude ??
        legacy?.lng ??
        legacy?.lon ??
        legacy?.longitude ??
        fallback?.facility?.lng ??
        fallback?.facility?.longitude ??
        null,
    };
  }

  if (fallback?.facility || fallback?.facilityName) {
    return {
      id: fallback?.facilityId || fallback?.facility?.id || appointmentRow?.practice_id || null,
      name: fallback?.facilityName || fallback?.facility?.name || "",
      address: fallback?.facility?.address || "",
      city: fallback?.facility?.city || "",
      country: fallback?.facility?.country || "",
      lat: fallback?.facility?.lat ?? fallback?.facility?.latitude ?? null,
      lng:
        fallback?.facility?.lng ??
        fallback?.facility?.lon ??
        fallback?.facility?.longitude ??
        null,
    };
  }

  return null;
}

function buildDoctorCard({
  doctorId,
  directoryDoctor,
  doctorProfile,
  authProfile,
  publicDoctor,
  primaryService,
  facility,
  cachedDoctor,
  fallbackDoctor,
}) {
  const cached = cachedDoctor || {};
  const fallback = fallbackDoctor || {};
  const directory = directoryDoctor || {};
  const resolvedDoctorId = String(
    doctorId ||
      directory?.doctor_user_id ||
      doctorProfile?.user_id ||
      publicDoctor?.doctor_user_id ||
      authProfile?.user_id ||
      cached?.id ||
      fallback?.id ||
      ""
  ).trim();
  const favoriteDoctorIds = Array.from(
    new Set(
      [
        doctorId,
        resolvedDoctorId,
        directory?.doctor_user_id,
        directory?.user_id,
        directory?.id,
        doctorProfile?.user_id,
        doctorProfile?.id,
        publicDoctor?.doctor_user_id,
        publicDoctor?.user_id,
        publicDoctor?.id,
        authProfile?.user_id,
        authProfile?.id,
        cached?.id,
        cached?.user_id,
        cached?.doctor_user_id,
        cached?.profile_id,
        fallback?.id,
        fallback?.user_id,
        fallback?.doctor_user_id,
        fallback?.profile_id,
        fallback?.doctor?.id,
        fallback?.doctor?.user_id,
        fallback?.doctor?.doctor_user_id,
        fallback?.doctor?.profile_id,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
  const avatar = resolveDoctorAvatar(
    directory?.profile_image_url ||
      directory?.avatar_url ||
      doctorProfile?.profile_image_url ||
      doctorProfile?.avatar_url ||
      publicDoctor?.profile_image_url ||
      publicDoctor?.avatar_url,
    directory?.profile_photo_path ||
      doctorProfile?.profile_photo_path ||
      publicDoctor?.profile_photo_path,
    authProfile?.profile_image_url ||
      authProfile?.avatar_url ||
      directory?.avatar ||
      cached?.avatar ||
      fallback?.avatar ||
      "/icons/doctor/avatar_placeholder.svg"
  );

  const yearsExperienceRaw =
    directory?.years_of_experience ??
    directory?.years_experience ??
    publicDoctor?.years_of_experience ??
    doctorProfile?.years_experience ??
    doctorProfile?.years_of_experience ??
    cached?.yearsExperience ??
    cached?.years_of_experience ??
    fallback?.yearsExperience ??
    fallback?.years_experience ??
    null;
  const yearsExperience =
    yearsExperienceRaw == null || yearsExperienceRaw === ""
      ? "—"
      : String(yearsExperienceRaw).trim();

  const ratingRaw = directory?.rating ?? doctorProfile?.rating ?? cached?.rating ?? fallback?.rating ?? null;
  const numericRating = Number(ratingRaw);
  const rating = Number.isFinite(numericRating) && numericRating > 0 ? numericRating.toFixed(1) : "—";
  const verificationStatus = normalizeVerificationStatus(
    directory?.verified_status ||
      publicDoctor?.verified_status ||
      doctorProfile?.verified_status ||
      cached?.verificationStatus ||
      fallback?.verificationStatus,
    cached?.verified || fallback?.verified ? "verified" : "pending"
  );

  const facilityName =
    facility?.name ||
    directory?.facility_name ||
    directory?.hospital ||
    cached?.hospital ||
    cached?.facility?.name ||
    fallback?.hospital ||
    fallback?.facility?.name ||
    "";
  const facilityAddress =
    [facility?.address, facility?.city, facility?.country].filter(Boolean).join(", ") ||
    directory?.facility_address ||
    cached?.address ||
    fallback?.address ||
    "";
  const servicePriceText = String(fallback?.serviceLabelPrice || fallback?.serviceNamePrice || "").trim();
  const resolvedCurrency =
    cached?.currency ||
    fallback?.currency ||
    primaryService?.currency ||
    fallback?.doctor?.currency ||
    "ZAR";

  return {
    id: resolvedDoctorId || favoriteDoctorIds[0] || null,
    doctor_user_id:
      resolvedDoctorId ||
      String(directory?.doctor_user_id || publicDoctor?.doctor_user_id || fallback?.doctor_user_id || "").trim() ||
      null,
    user_id:
      String(
        doctorProfile?.user_id ||
          directory?.user_id ||
          publicDoctor?.user_id ||
          authProfile?.user_id ||
          fallback?.user_id ||
          resolvedDoctorId ||
          ""
      ).trim() || null,
    profile_id:
      doctorProfile?.id ||
      directory?.id ||
      publicDoctor?.id ||
      fallback?.profile_id ||
      fallback?.doctor?.profile_id ||
      null,
    name: resolvePatientFacingDoctorName(
      directory,
      publicDoctor,
      doctorProfile,
      authProfile,
      cached,
      fallback
    ),
    specialty: (
      normalizeSpecialty(
        directory?.primary_specialty ||
          directory?.specialties ||
          directory?.specialization ||
          directory?.specialty ||
          publicDoctor?.primary_specialty ||
          publicDoctor?.specialties ||
          publicDoctor?.specialization ||
          publicDoctor?.specialty ||
          doctorProfile?.primary_specialty ||
          doctorProfile?.specialties ||
          doctorProfile?.specialization ||
          doctorProfile?.specialty ||
          cached?.primary_specialty ||
          cached?.specialties ||
          cached?.specialization ||
          cached?.specialty ||
          fallback?.primary_specialty ||
          fallback?.specialties ||
          fallback?.specialization ||
          fallback?.specialty
      ) !== "Doctor"
        ? normalizeSpecialty(
            directory?.primary_specialty ||
              directory?.specialties ||
              directory?.specialization ||
              directory?.specialty ||
              publicDoctor?.primary_specialty ||
              publicDoctor?.specialties ||
              publicDoctor?.specialization ||
            publicDoctor?.specialty ||
              doctorProfile?.primary_specialty ||
              doctorProfile?.specialties ||
              doctorProfile?.specialization ||
              doctorProfile?.specialty ||
              cached?.primary_specialty ||
              cached?.specialties ||
              cached?.specialization ||
              cached?.specialty ||
              fallback?.primary_specialty ||
              fallback?.specialties ||
              fallback?.specialization ||
              fallback?.specialty
          )
        : hasStrongDoctorText(cached?.specialty, "Doctor")
          ? cached?.specialty
          : fallback?.specialty || "Doctor"
    ),
    yearsExperience,
    rating,
    verified: verificationStatus === "verified",
    verificationStatus,
    practice_mode:
      directory?.practice_mode ||
      doctorProfile?.practice_mode ||
      publicDoctor?.practice_mode ||
      cached?.practice_mode ||
      cached?.practiceMode ||
      fallback?.practice_mode ||
      fallback?.practiceMode ||
      null,
    practiceMode:
      directory?.practice_mode ||
      doctorProfile?.practice_mode ||
      publicDoctor?.practice_mode ||
      cached?.practiceMode ||
      cached?.practice_mode ||
      fallback?.practiceMode ||
      fallback?.practice_mode ||
      null,
    avatar,
    profile_photo_path:
      directory?.profile_photo_path ||
      doctorProfile?.profile_photo_path ||
      publicDoctor?.profile_photo_path ||
      fallback?.profile_photo_path ||
      fallback?.doctor?.profile_photo_path ||
      "",
    profile_image_url:
      directory?.profile_image_url ||
      doctorProfile?.profile_image_url ||
      publicDoctor?.profile_image_url ||
      fallback?.profile_image_url ||
      fallback?.doctor?.profile_image_url ||
      "",
    isFavorite: isFavoriteDoctor(favoriteDoctorIds),
    hospital: facilityName || fallback?.hospital || "Consultation location",
    address: facilityAddress || fallback?.address || "",
    serviceLabel: fallback?.serviceLabel || cached?.serviceLabel || primaryService?.name || "Consultation",
    serviceLabelPrice:
      servicePriceText ||
      (Number.isFinite(Number(directory?.service_price))
        ? Number(directory.service_price).toFixed(2)
        : "") ||
      cached?.serviceLabelPrice ||
      (Number.isFinite(Number(primaryService?.price))
        ? Number(primaryService.price).toFixed(2)
        : ""),
    serviceName: fallback?.serviceName || cached?.serviceName || primaryService?.name || "",
    serviceNamePrice:
      fallback?.serviceNamePrice ||
      cached?.serviceNamePrice ||
      (Number.isFinite(Number(directory?.secondary_service_price))
        ? `${Number(directory.secondary_service_price).toFixed(2)} ${resolvedCurrency}`
        : "") ||
      (Number.isFinite(Number(primaryService?.price))
        ? `${Number(primaryService.price).toFixed(2)} ${resolvedCurrency}`
        : ""),
    currency: resolvedCurrency,
    languages:
      normalizeLanguages(directory?.languages_spoken) ||
      normalizeLanguages(doctorProfile?.languages_spoken) ||
      normalizeLanguages(publicDoctor?.languages_spoken) ||
      normalizeLanguages(cached?.languages) ||
      normalizeLanguages(fallback?.languages) ||
      "",
    facility_id: facility?.id || fallback?.facility_id || fallback?.facility?.id || null,
    facility:
      facility || fallback?.facility
        ? {
            id: facility?.id || fallback?.facility?.id || fallback?.facility_id || null,
            name: facility?.name || fallback?.facility?.name || facilityName || "",
            address: facility?.address || fallback?.facility?.address || "",
            city: facility?.city || fallback?.facility?.city || "",
            country: facility?.country || fallback?.facility?.country || "",
            lat:
              facility?.lat ??
              facility?.latitude ??
              fallback?.facility?.lat ??
              fallback?.facility?.latitude ??
              null,
            lng:
              facility?.lng ??
              facility?.lon ??
              facility?.longitude ??
              fallback?.facility?.lng ??
              fallback?.facility?.longitude ??
              null,
          }
        : null,
  };
}

function buildSnapshotFromRow(appointmentRow, context, fallback = null) {
  const dateISO = toDateISO(appointmentRow?.appointment_date) || fallback?.selectedDate || fallback?.when?.dateISO || null;
  const time = toHHMM(appointmentRow?.appointment_date) || fallback?.selectedTime || fallback?.when?.time || null;
  const duration = Number(appointmentRow?.duration_minutes || fallback?.when?.duration_mins || fallback?.duration || 30) || 30;
  const rawDoctorId = String(
    appointmentRow?.doctor_id || fallback?.doctor_id || fallback?.doctor?.id || ""
  ).trim();
  const doctorId = context.profileIdToUserId.get(rawDoctorId) || rawDoctorId;
  const facility = resolveFacility(
    { ...(appointmentRow || {}), doctor_id: doctorId || appointmentRow?.doctor_id || null },
    context.consultanciesById,
    context.firstConsultancyByDoctor,
    context.legacyByDoctor,
    fallback
  );
  const mode = normalizeMode(appointmentRow?.consultation_type, fallback?.mode, facility);
  const cachedDoctor = readStoredDoctorCache(doctorId) || readStoredDoctorCache(rawDoctorId);
  const services = context.servicesByDoctorId.get(doctorId) || [];
  const bookedServiceId = String(
    appointmentRow?.booked_service_id || fallback?.serviceId || ""
  ).trim();
  const bookedServiceName = String(
    appointmentRow?.booked_service_name ||
      fallback?.serviceName ||
      fallback?.serviceLabel ||
      ""
  ).trim();
  const bookedServiceCurrency = String(
    appointmentRow?.booked_service_currency || fallback?.currency || ""
  ).trim();
  const bookedServicePrice = Number(
    appointmentRow?.booked_service_price ?? fallback?.amountPaid
  );
  let matchedService =
    services.find((service) => String(service?.id || "").trim() === bookedServiceId) || null;
  if (!matchedService && bookedServiceName) {
    const normalizedBookedServiceName = bookedServiceName.toLowerCase();
    matchedService =
      services.find(
        (service) =>
          String(service?.name || "").trim().toLowerCase() === normalizedBookedServiceName
      ) || null;
  }
  const resolvedServiceName = !isGenericServiceName(bookedServiceName)
    ? bookedServiceName
    : String(matchedService?.name || "").trim() ||
      String(fallback?.serviceName || fallback?.serviceLabel || "").trim() ||
      String(cachedDoctor?.serviceName || cachedDoctor?.serviceLabel || "").trim() ||
      String(context.primaryServiceByDoctorId.get(doctorId)?.name || "").trim() ||
      (mode === "evisit" ? "Virtual" : "Consultation");
  const resolvedServiceCurrency =
    bookedServiceCurrency ||
    String(matchedService?.currency || "").trim() ||
    String(fallback?.currency || "").trim() ||
    String(cachedDoctor?.currency || "").trim() ||
    String(context.primaryServiceByDoctorId.get(doctorId)?.currency || "").trim() ||
    "ZAR";
  const resolvedAmountPaid = Number.isFinite(bookedServicePrice)
    ? bookedServicePrice
    : Number.isFinite(Number(matchedService?.price))
      ? Number(matchedService.price)
      : null;
  const resolvedServicePriceText =
    String(fallback?.serviceNamePrice || fallback?.serviceLabelPrice || "").trim() ||
    formatAmount(resolvedAmountPaid, resolvedServiceCurrency);
  const doctor = buildDoctorCard({
    doctorId,
    directoryDoctor: context.directoryDoctorsById.get(doctorId) || null,
    doctorProfile: context.doctorProfilesById.get(doctorId) || null,
    authProfile: context.authProfilesById.get(doctorId) || null,
    publicDoctor: context.publicDoctorsById.get(doctorId) || null,
    primaryService: context.primaryServiceByDoctorId.get(doctorId) || null,
    facility,
    cachedDoctor,
    fallbackDoctor: fallback?.doctor || null,
  });
  const doctorWithBookedService = {
    ...doctor,
    name:
      hasStrongDoctorText(doctor?.name, "Doctor")
        ? doctor.name
        : resolvePatientFacingDoctorName(
            fallback?.doctorName,
            fallback?.doctor,
            cachedDoctor
          ),
    serviceLabel: resolvedServiceName || doctor?.serviceLabel || "Consultation",
    serviceName: resolvedServiceName || doctor?.serviceName || "",
    serviceLabelPrice:
      resolvedServicePriceText || doctor?.serviceLabelPrice || "",
    serviceNamePrice:
      resolvedServicePriceText || doctor?.serviceNamePrice || "",
    currency: resolvedServiceCurrency || doctor?.currency || "ZAR",
  };
  const fallbackAmount = Number(fallback?.amountPaid);
  const amountPaid = resolvedAmountPaid != null
    ? resolvedAmountPaid
    : Number.isFinite(fallbackAmount)
    ? fallbackAmount
    : Number.isFinite(Number(context.primaryServiceByDoctorId.get(doctorId)?.price))
      ? Number(context.primaryServiceByDoctorId.get(doctorId)?.price)
    : Number.isFinite(Number(context.doctorProfilesById.get(doctorId)?.consultation_fee))
      ? Number(context.doctorProfilesById.get(doctorId)?.consultation_fee)
      : null;
  const currency =
    fallback?.currency ||
    fallback?.doctor?.currency ||
    doctor?.currency ||
    "ZAR";

  return {
    ...(fallback || {}),
    id: String(appointmentRow?.id || fallback?.id || ""),
    appointmentId: String(appointmentRow?.id || fallback?.appointmentId || ""),
    careSubjectType:
      String(
        appointmentRow?.care_subject_type ||
          fallback?.careSubjectType ||
          fallback?.care_subject_type ||
          "self",
      )
        .trim()
        .toLowerCase() || "self",
    careSubjectId:
      appointmentRow?.care_subject_dependant_id ||
      fallback?.careSubjectId ||
      fallback?.care_subject_dependant_id ||
      null,
    careSubjectName:
      appointmentRow?.care_subject_name ||
      fallback?.careSubjectName ||
      fallback?.care_subject_name ||
      null,
    careSubjectRelationship:
      appointmentRow?.care_subject_relationship ||
      fallback?.careSubjectRelationship ||
      fallback?.care_subject_relationship ||
      null,
    guardianUserId:
      appointmentRow?.guardian_user_id ||
      fallback?.guardianUserId ||
      fallback?.guardian_user_id ||
      null,
    guardianName:
      fallback?.guardianName ||
      fallback?.guardian_name ||
      null,
    responsibleRelationship:
      appointmentRow?.responsible_relationship ||
      fallback?.responsibleRelationship ||
      fallback?.responsible_relationship ||
      null,
    doctor_id: doctor?.id || appointmentRow?.doctor_id || fallback?.doctor_id || null,
    doctorName:
      (hasStrongDoctorText(doctorWithBookedService?.name, "Doctor")
        ? doctorWithBookedService?.name
        : resolvePatientFacingDoctorName(
            fallback?.doctorName,
            fallback?.doctor,
            cachedDoctor,
            doctor?.name
          )) ||
      "Doctor",
    practice_id: appointmentRow?.practice_id || fallback?.practice_id || facility?.id || null,
    consultation_type: appointmentRow?.consultation_type || fallback?.consultation_type || null,
    status: String(appointmentRow?.status || fallback?.status || "scheduled").toLowerCase(),
    patientJoinedAt:
      String(appointmentRow?.patient_joined_at || fallback?.patientJoinedAt || fallback?.patient_joined_at || "").trim() || null,
    status_reason:
      String(
        appointmentRow?.status_reason ||
        fallback?.status_reason ||
        fallback?.appointmentStatusReason ||
        ""
      ).trim().toLowerCase(),
    superseded_by_appointment_id:
      appointmentRow?.superseded_by_appointment_id ||
      fallback?.superseded_by_appointment_id ||
      null,
    notes: appointmentRow?.notes || fallback?.notes || "",
    diagnosisTitle:
      pickDiagnosisTitle(
        appointmentRow?.diagnosis_title,
        appointmentRow?.diagnosisTitle,
        fallback?.diagnosisTitle,
        fallback?.diagnosis,
      ) || "",
    diagnosis:
      pickDiagnosisTitle(
        appointmentRow?.diagnosis_title,
        appointmentRow?.diagnosisTitle,
        fallback?.diagnosisTitle,
        fallback?.diagnosis,
      ) || "",
    diagnosisNotes:
      pickDiagnosisNotes(
        appointmentRow?.diagnosis_notes,
        appointmentRow?.diagnosisNotes,
        fallback?.diagnosisNotes,
      ) || "",
    selectedDate: dateISO,
    selectedTime: time,
    dateISO,
    start: time,
    end: plusMinutesHHMM(time, duration),
    when: {
      ...(fallback?.when || {}),
      dateISO,
      time,
      start: time,
      end: plusMinutesHHMM(time, duration),
      duration_mins: duration,
    },
    duration_minutes: duration,
    durationMinutes: duration,
    mode,
    meetUrl:
      mode === "evisit"
        ? fallback?.meetUrl || "https://meet.google.com/hai-wgqq-pg"
        : "",
    doctor: doctorWithBookedService,
    serviceId: bookedServiceId || matchedService?.id || fallback?.serviceId || null,
    serviceName: resolvedServiceName,
    serviceLabel: resolvedServiceName,
    serviceNamePrice: resolvedServicePriceText,
    facilityId: facility?.id || fallback?.facilityId || fallback?.facility?.id || null,
    facilityName:
      facility?.name ||
      fallback?.facilityName ||
      fallback?.facility?.name ||
      doctor?.hospital ||
      "Consultation location",
    facility: facility || fallback?.facility || doctor?.facility || null,
    paymentMethodLabel:
      fallback?.paymentMethodLabel ||
      fallback?.paymentLabel ||
      (appointmentRow?.is_paid ? "Debit / Credit cards" : "Payment pending"),
    paymentLabel:
      fallback?.paymentLabel ||
      fallback?.paymentMethodLabel ||
      (appointmentRow?.is_paid ? "Debit / Credit cards" : "Payment pending"),
    insurancePaymentMode: fallback?.insurancePaymentMode || "self_pay",
    insuranceProvider: fallback?.insuranceProvider || "",
    insuranceContextLabel: fallback?.insuranceContextLabel || "",
    insuranceProviderOptions: Array.isArray(fallback?.insuranceProviderOptions)
      ? fallback.insuranceProviderOptions
      : [],
    insuranceAcceptedLabel: fallback?.insuranceAcceptedLabel || "",
    currency: resolvedServiceCurrency || currency,
    amountPaid: Number.isFinite(amountPaid) ? amountPaid : fallback?.amountPaid ?? null,
    payment_intent_id: appointmentRow?.payment_intent_id || fallback?.payment_intent_id || null,
    is_paid:
      typeof appointmentRow?.is_paid === "boolean" ? appointmentRow.is_paid : !!fallback?.is_paid,
  };
}

function readStoredAppointmentSnapshot(appointmentId) {
  if (typeof window === "undefined" || !appointmentId) return null;
  try {
    const raw = sessionStorage.getItem(`afrimed:appointment:${String(appointmentId)}`);
    return raw ? safeJSONParse(raw) : null;
  } catch {
    return null;
  }
}

function mergeDoctorSnapshot(primaryDoctor, fallbackDoctor) {
  const primary = primaryDoctor || {};
  const fallback = fallbackDoctor || {};
  const favoriteDoctorIds = [
    primary.id,
    primary.user_id,
    primary.doctor_user_id,
    primary.profile_id,
    fallback.id,
    fallback.user_id,
    fallback.doctor_user_id,
    fallback.profile_id,
  ].filter(Boolean);
  return {
    ...fallback,
    ...primary,
    id: primary.id || fallback.id || null,
    name:
      primary.name && primary.name !== "Doctor"
        ? primary.name
        : fallback.name || primary.name || "Doctor",
    specialty:
      primary.specialty && primary.specialty !== "Doctor"
        ? primary.specialty
        : fallback.specialty || primary.specialty || "Doctor",
    yearsExperience:
      primary.yearsExperience && primary.yearsExperience !== "—"
        ? primary.yearsExperience
        : fallback.yearsExperience || primary.yearsExperience || "—",
    rating:
      primary.rating && primary.rating !== "—"
        ? primary.rating
        : fallback.rating || primary.rating || "—",
    avatar: primary.avatar || fallback.avatar || "",
    languages: primary.languages || fallback.languages || "",
    serviceLabel: primary.serviceLabel || fallback.serviceLabel || "Consultation",
    serviceLabelPrice: primary.serviceLabelPrice || fallback.serviceLabelPrice || "",
    serviceName: primary.serviceName || fallback.serviceName || "",
    serviceNamePrice: primary.serviceNamePrice || fallback.serviceNamePrice || "",
    currency: primary.currency || fallback.currency || "ZAR",
    practice_mode: primary.practice_mode || primary.practiceMode || fallback.practice_mode || fallback.practiceMode || null,
    practiceMode: primary.practiceMode || primary.practice_mode || fallback.practiceMode || fallback.practice_mode || null,
    hospital: primary.hospital || fallback.hospital || "Consultation location",
    address: primary.address || fallback.address || "",
    facility_id: primary.facility_id || fallback.facility_id || null,
    facility: primary.facility || fallback.facility || null,
    isFavorite:
      primary.isFavorite === true ||
      fallback.isFavorite === true ||
      isFavoriteDoctor(favoriteDoctorIds),
  };
}

export function mergeDistinctAppointmentSnapshots(...collections) {
  const merged = [];
  const indexById = new Map();

  collections.forEach((collection) => {
    const rows = Array.isArray(collection) ? collection : [];
    rows.forEach((row) => {
      const id = String(row?.id || "").trim();
      if (!id) {
        merged.push(row);
        return;
      }

      const existingIndex = indexById.get(id);
      if (existingIndex == null) {
        indexById.set(id, merged.length);
        merged.push(row);
        return;
      }

      const existing = merged[existingIndex] || {};
      merged[existingIndex] = {
        ...row,
        ...existing,
        doctor: mergeDoctorSnapshot(existing?.doctor, row?.doctor),
      };
    });
  });

  return merged;
}

function mergeSnapshotWithStored(primarySnapshot, storedSnapshot) {
  if (!storedSnapshot) return primarySnapshot;
  if (!primarySnapshot) return storedSnapshot;

  return {
    ...storedSnapshot,
    ...primarySnapshot,
    doctor: mergeDoctorSnapshot(primarySnapshot.doctor, storedSnapshot.doctor),
    amountPaid:
      primarySnapshot.amountPaid != null ? primarySnapshot.amountPaid : storedSnapshot.amountPaid,
    currency: primarySnapshot.currency || storedSnapshot.currency || "ZAR",
    paymentLabel: primarySnapshot.paymentLabel || storedSnapshot.paymentLabel || "",
    paymentMethodLabel:
      primarySnapshot.paymentMethodLabel || storedSnapshot.paymentMethodLabel || "",
    insurancePaymentMode:
      primarySnapshot.insurancePaymentMode || storedSnapshot.insurancePaymentMode || "self_pay",
    insuranceProvider:
      primarySnapshot.insuranceProvider || storedSnapshot.insuranceProvider || "",
    insuranceContextLabel:
      primarySnapshot.insuranceContextLabel || storedSnapshot.insuranceContextLabel || "",
  };
}

function deriveAppointmentStatusReason(notifications, appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) return "";

  for (const row of Array.isArray(notifications) ? notifications : []) {
    const type = String(row?.type || "").trim().toLowerCase();
    if (!type) continue;
    if (type.startsWith(`appointment_declined:${cleanAppointmentId.toLowerCase()}`)) {
      return "declined";
    }
    if (type.startsWith(`appointment_cancelled:${cleanAppointmentId.toLowerCase()}`)) {
      return "cancelled";
    }
  }

  return "";
}

function deriveCancellationRequestState(notifications, appointmentId) {
  const cleanAppointmentId = String(appointmentId || "").trim().toLowerCase();
  if (!cleanAppointmentId) return "";

  for (const row of Array.isArray(notifications) ? notifications : []) {
    const type = String(row?.type || "").trim().toLowerCase();
    if (!type) continue;

    if (type.startsWith(`appointment_cancellation_request:${cleanAppointmentId}:requested`)) {
      return "requested";
    }
    if (type.startsWith(`appointment_cancellation_request:${cleanAppointmentId}:cancelled`)) {
      return "cancelled";
    }
    if (
      type.startsWith(`appointment_cancellation_request:${cleanAppointmentId}:accepted`) ||
      type.startsWith(`appointment_cancelled:${cleanAppointmentId}`)
    ) {
      return "accepted";
    }
  }

  return "";
}

function derivePaymentDisplayState({ appointmentStatus, statusReason, paymentRow }) {
  const paymentStatus = normalizePaymentLedgerStatus(paymentRow?.status);
  const refundStatus = normalizePaymentLedgerStatus(paymentRow?.meta?.stripe_refund_status);

  if (paymentStatus === "cancelled" || paymentStatus === "refunded") return "cancelled";
  if (paymentStatus === "refund_pending" || refundStatus === "pending") return "refund_pending";
  if (paymentStatus === "refund_failed") return "reversal_required";
  if (paymentStatus === "paid") {
    return appointmentStatus === "cancelled" && statusReason === "declined"
      ? "reversal_required"
      : "paid";
  }

  if (appointmentStatus === "cancelled" && statusReason === "declined") {
    return paymentRow?.id ? "reversal_required" : "cancelled";
  }

  return "";
}

async function loadAppointmentDecorations({ appointmentId, appointmentStatus, statusReason = "" }) {
  const cleanAppointmentId = String(appointmentId || "").trim();
  if (!cleanAppointmentId) {
    return {
      paymentRow: null,
      statusReason: "",
      cancellationRequestState: "",
      paymentDisplayState: "",
    };
  }

  let authUserId = "";
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    authUserId = String(user?.id || "").trim();
  } catch {
    authUserId = "";
  }

  const [paymentResult, notificationResult] = await Promise.all([
    supabase
      .from("payments")
      .select(
        "id,status,method,stripe_payment_intent_id,stripe_customer_id,meta,updated_at"
      )
      .eq("appointment_id", cleanAppointmentId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle(),
    authUserId
      ? supabase
          .from("notifications")
          .select("type,created_at")
          .eq("user_id", authUserId)
          .ilike("type", `%${cleanAppointmentId}%`)
          .order("created_at", { ascending: false })
          .limit(12)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if (paymentResult?.error) throw paymentResult.error;
  if (notificationResult?.error) throw notificationResult.error;

  const paymentRow = paymentResult?.data || null;
  const notificationRows = notificationResult?.data || [];
  const resolvedStatusReason =
    String(statusReason || "").trim().toLowerCase() ||
    deriveAppointmentStatusReason(notificationRows, cleanAppointmentId);
  const cancellationRequestState = deriveCancellationRequestState(
    notificationRows,
    cleanAppointmentId,
  );
  const paymentDisplayState = derivePaymentDisplayState({
    appointmentStatus,
    statusReason: resolvedStatusReason,
    paymentRow,
  });

  return {
    paymentRow,
    statusReason: resolvedStatusReason,
    cancellationRequestState,
    paymentDisplayState,
  };
}

const DOCTOR_CONTEXT_CACHE_TTL_MS = 60_000;
const doctorContextCache = new Map();

// This projection must stay within the canonical doctor_profiles schema. In
// particular, avatar_url belongs to profiles, not doctor_profiles. Selecting
// it here makes PostgREST reject the entire targeted query, leaving appointment
// cards with their fallback avatar even when the clinician has a headshot.
const DOCTOR_PROFILE_CONTEXT_COLUMNS =
  "id,user_id,full_name,primary_specialty,specialties,specialization,specialty,consultation_fee,profile_image_url,profile_photo_path,verified_status,years_experience,years_of_experience,languages_spoken,rating,practice_mode";

function emptyDoctorContext() {
  return {
    profileIdToUserId: new Map(),
    directoryDoctorsById: new Map(),
    doctorProfilesById: new Map(),
    authProfilesById: new Map(),
    publicDoctorsById: new Map(),
    primaryServiceByDoctorId: new Map(),
    servicesByDoctorId: new Map(),
    consultanciesById: new Map(),
    firstConsultancyByDoctor: new Map(),
    legacyByDoctor: new Map(),
  };
}

async function loadDoctorContext(doctorIds) {
  const ids = Array.from(new Set((doctorIds || []).map((id) => String(id || "").trim()).filter(Boolean)));
  if (ids.length === 0) {
    return emptyDoctorContext();
  }

  const cacheKey = [...ids].sort().join(",");
  const cached = doctorContextCache.get(cacheKey);
  if (cached && Date.now() - cached.createdAt < DOCTOR_CONTEXT_CACHE_TTL_MS) {
    return cached.promise;
  }

  const promise = loadDoctorContextUncached(ids);
  doctorContextCache.set(cacheKey, { createdAt: Date.now(), promise });
  try {
    return await promise;
  } catch (error) {
    doctorContextCache.delete(cacheKey);
    throw error;
  }
}

async function loadDoctorContextUncached(ids) {

  const [
    { data: doctorProfilesByUserId },
    { data: doctorProfilesByProfileId },
    { data: authProfiles },
    { data: publicDoctors },
    { data: doctorServices },
    { data: consultancies },
    { data: legacyLocations },
  ] = await Promise.all([
    supabase
      .from("doctor_profiles")
      .select(DOCTOR_PROFILE_CONTEXT_COLUMNS)
      .in("user_id", ids),
    supabase
      .from("doctor_profiles")
      .select(DOCTOR_PROFILE_CONTEXT_COLUMNS)
      .in("id", ids),
    supabase
      .from("profiles")
      .select("user_id,display_name,avatar_url")
      .in("user_id", ids),
    supabase
      .from("v_doctor_directory_browse")
      .select("doctor_user_id,display_name,specialty,verified_status,profile_photo_path,years_of_experience,languages_spoken,facility_name,facility_address,facility_city,facility_country")
      .in("doctor_user_id", ids),
    supabase
      .from("doctor_services")
      .select("doctor_id,name,price,currency,duration_minutes,is_active,sort_order,updated_at,created_at")
      .in("doctor_id", ids)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false })
      .order("created_at", { ascending: false }),
    supabase
      .from("doctor_consultancies")
      .select("id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at")
      .in("doctor_id", ids)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false }),
    supabase
      .from("doctor_consulting_locations")
      .select("id,doctor_id,facility_name,street_address,city,country,updated_at")
      .in("doctor_id", ids)
      .order("updated_at", { ascending: false }),
  ]);

  const profileRows = [
    ...(Array.isArray(doctorProfilesByUserId) ? doctorProfilesByUserId : []),
    ...(Array.isArray(doctorProfilesByProfileId) ? doctorProfilesByProfileId : []),
  ];
  const profileIdToUserId = new Map(
    profileRows
      .map((row) => [String(row?.id || "").trim(), String(row?.user_id || "").trim()])
      .filter(([profileId, userId]) => !!profileId && !!userId)
  );
  const canonicalIds = Array.from(new Set(ids.map((id) => profileIdToUserId.get(id) || id).filter(Boolean)));
  const expandedIds = canonicalIds.filter((id) => !ids.includes(id));
  const [
    { data: authProfilesExpanded },
    { data: publicDoctorsExpanded },
    { data: doctorServicesExpanded },
    { data: consultanciesExpanded },
    { data: legacyLocationsExpanded },
  ] = expandedIds.length ? await Promise.all([
    supabase
      .from("profiles")
      .select("user_id,display_name,avatar_url")
      .in("user_id", expandedIds),
    supabase
      .from("v_doctor_directory_browse")
      .select("doctor_user_id,display_name,specialty,verified_status,profile_photo_path,years_of_experience,languages_spoken,facility_name,facility_address,facility_city,facility_country")
      .in("doctor_user_id", expandedIds),
    supabase
      .from("doctor_services")
      .select("doctor_id,name,price,currency,duration_minutes,is_active,sort_order,updated_at,created_at")
      .in("doctor_id", expandedIds)
      .eq("is_active", true)
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false })
      .order("created_at", { ascending: false }),
    supabase
      .from("doctor_consultancies")
      .select("id,doctor_id,facility_name,facility_address,facility_city,facility_country,is_primary,sort_order,updated_at")
      .in("doctor_id", expandedIds)
      .order("is_primary", { ascending: false })
      .order("sort_order", { ascending: true })
      .order("updated_at", { ascending: false }),
    supabase
      .from("doctor_consulting_locations")
      .select("id,doctor_id,facility_name,street_address,city,country,updated_at")
      .in("doctor_id", expandedIds)
      .order("updated_at", { ascending: false }),
  ]) : [
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
    { data: [] },
  ];
  const doctorProfilesById = new Map();
  profileRows.forEach((row) => {
    const key = String(row?.user_id || "").trim();
    if (!key || doctorProfilesById.has(key)) return;
    doctorProfilesById.set(key, row);
  });
  const authProfilesById = new Map(
    [...(Array.isArray(authProfiles) ? authProfiles : []), ...(Array.isArray(authProfilesExpanded) ? authProfilesExpanded : [])]
      .map((row) => [String(row?.user_id || "").trim(), row])
      .filter(([id]) => !!id)
  );
  const publicDoctorsById = new Map(
    [...(Array.isArray(publicDoctors) ? publicDoctors : []), ...(Array.isArray(publicDoctorsExpanded) ? publicDoctorsExpanded : [])]
      .map((row) => [String(row?.doctor_user_id || "").trim(), row])
      .filter(([id]) => !!id)
  );
  const directoryDoctorsById = new Map(
    [...(Array.isArray(publicDoctors) ? publicDoctors : []), ...(Array.isArray(publicDoctorsExpanded) ? publicDoctorsExpanded : [])]
      .filter((row) => canonicalIds.includes(String(row?.doctor_user_id || "").trim()))
      .map((row) => [String(row?.doctor_user_id || "").trim(), row])
      .filter(([id]) => !!id)
  );
  const primaryServiceByDoctorId = new Map();
  const servicesByDoctorId = new Map();
  [...(Array.isArray(doctorServices) ? doctorServices : []), ...(Array.isArray(doctorServicesExpanded) ? doctorServicesExpanded : [])].forEach((row) => {
    const rawDoctorId = String(row?.doctor_id || "").trim();
    const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
    if (!doctorId) return;
    if (!primaryServiceByDoctorId.has(doctorId)) {
      primaryServiceByDoctorId.set(doctorId, row);
    }
    const list = servicesByDoctorId.get(doctorId) || [];
    list.push(row);
    servicesByDoctorId.set(doctorId, list);
  });
  const consultanciesById = new Map();
  const firstConsultancyByDoctor = new Map();
  [...(Array.isArray(consultancies) ? consultancies : []), ...(Array.isArray(consultanciesExpanded) ? consultanciesExpanded : [])].forEach((row) => {
    const rowId = String(row?.id || "").trim();
    const rawDoctorId = String(row?.doctor_id || "").trim();
    const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
    if (rowId) consultanciesById.set(rowId, row);
    if (doctorId && !firstConsultancyByDoctor.has(doctorId)) {
      firstConsultancyByDoctor.set(doctorId, row);
    }
  });
  const legacyByDoctor = new Map();
  [...(Array.isArray(legacyLocations) ? legacyLocations : []), ...(Array.isArray(legacyLocationsExpanded) ? legacyLocationsExpanded : [])].forEach((row) => {
    const rawDoctorId = String(row?.doctor_id || "").trim();
    const doctorId = profileIdToUserId.get(rawDoctorId) || rawDoctorId;
    if (doctorId && !legacyByDoctor.has(doctorId)) {
      legacyByDoctor.set(doctorId, row);
    }
  });

  return {
    profileIdToUserId,
    directoryDoctorsById,
    doctorProfilesById,
    authProfilesById,
    publicDoctorsById,
    primaryServiceByDoctorId,
    servicesByDoctorId,
    consultanciesById,
    firstConsultancyByDoctor,
    legacyByDoctor,
  };
}

export async function loadPatientAppointmentSnapshot(appointmentId, options = {}) {
  if (!appointmentId) return null;

  const patientId = await getCurrentPatientId();
  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,patient_id,doctor_id,appointment_date,duration_minutes,status,status_reason,superseded_by_appointment_id,consultation_type,is_paid,payment_intent_id,notes,diagnosis_title,diagnosis_notes,practice_id,booked_service_id,booked_service_name,booked_service_price,booked_service_currency,care_subject_type,care_subject_dependant_id,care_subject_name,care_subject_relationship,care_subject_date_of_birth,guardian_user_id,booked_by_user_id,responsible_user_id,responsible_relationship,video_status,video_started_at,video_ended_at"
    )
    .eq("id", appointmentId)
    .eq("patient_id", patientId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const [context, participationByAppointmentId] = await Promise.all([
    loadDoctorContext([data?.doctor_id]),
    loadPatientParticipationByAppointmentIds([data?.id]),
  ]);
  const snapshot = buildSnapshotFromRow(
    {
      ...data,
      patient_joined_at: participationByAppointmentId.get(String(data?.id || "").trim()) || null,
    },
    context,
    options.fallback || null,
  );
  const decorations = await loadAppointmentDecorations({
    appointmentId: data?.id,
    appointmentStatus: data?.status,
    statusReason: data?.status_reason,
  });
  const paymentFallbackLabel =
    snapshot?.paymentMethodLabel ||
    snapshot?.paymentLabel ||
    (data?.is_paid ? "Debit / Credit cards" : "Payment pending");
  const paymentMethodLabel = prettyPaymentMethod(
    decorations?.paymentRow?.method,
    paymentFallbackLabel,
  );

  const enrichedSnapshot = {
    ...snapshot,
    paymentLabel: paymentMethodLabel,
    paymentMethodLabel,
    paymentLedgerStatus: normalizePaymentLedgerStatus(decorations?.paymentRow?.status),
    paymentDisplayState: decorations?.paymentDisplayState || "",
    appointmentStatusReason: decorations?.statusReason || "",
    cancellationRequestState: decorations?.cancellationRequestState || "",
    paymentMeta:
      decorations?.paymentRow?.meta && typeof decorations.paymentRow.meta === "object"
        ? { ...decorations.paymentRow.meta }
        : {},
    insurancePaymentMode:
      decorations?.paymentRow?.meta?.insurance_payment_mode ||
      snapshot?.insurancePaymentMode ||
      "self_pay",
    insuranceProvider:
      decorations?.paymentRow?.meta?.insurance_provider ||
      snapshot?.insuranceProvider ||
      "",
    insuranceContextLabel:
      decorations?.paymentRow?.meta?.insurance_context_label ||
      snapshot?.insuranceContextLabel ||
      "",
    payment_intent_id:
      decorations?.paymentRow?.stripe_payment_intent_id ||
      snapshot?.payment_intent_id ||
      null,
  };

  return mergeSnapshotWithStored(
    enrichedSnapshot,
    readStoredAppointmentSnapshot(data?.id),
  );
}

export async function listMyPastAppointmentSnapshots() {
  const patientId = await getCurrentPatientId();
  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,patient_id,doctor_id,appointment_date,duration_minutes,status,status_reason,superseded_by_appointment_id,consultation_type,is_paid,payment_intent_id,notes,diagnosis_title,diagnosis_notes,practice_id,booked_service_id,booked_service_name,booked_service_price,booked_service_currency,care_subject_type,care_subject_dependant_id,care_subject_name,care_subject_relationship,care_subject_date_of_birth,guardian_user_id,booked_by_user_id,responsible_user_id,responsible_relationship"
    )
    .eq("patient_id", patientId)
    .is("superseded_by_appointment_id", null)
    .order("appointment_date", { ascending: false });

  if (error) throw error;

  const nowMs = Date.now();
  const rows = (Array.isArray(data) ? data : []).filter((row) =>
    shouldAppearInPastAppointmentsList(row, nowMs)
  );
  const [context, participationByAppointmentId] = await Promise.all([
    loadDoctorContext(rows.map((row) => row?.doctor_id)),
    loadPatientParticipationByAppointmentIds(rows.map((row) => row?.id)),
  ]);
  return rows.map((row) =>
    mergeSnapshotWithStored(
      buildSnapshotFromRow(
        { ...row, patient_joined_at: participationByAppointmentId.get(String(row?.id || "").trim()) || null },
        context,
        null,
      ),
      readStoredAppointmentSnapshot(row?.id),
    )
  );
}

export async function listPastAppointmentSnapshotsForPatient({
  patientId = null,
  patientUserId = null,
} = {}) {
  const patientRowId = String(patientId || "").trim();
  const patientAuthUserId = String(patientUserId || "").trim();

  let resolvedPatientId = patientRowId;
  if (!resolvedPatientId && patientAuthUserId) {
    const { data: patientRow, error: patientError } = await supabase
      .from("patients")
      .select("id")
      .eq("user_id", patientAuthUserId)
      .maybeSingle();

    if (patientError) throw patientError;
    resolvedPatientId = String(patientRow?.id || "").trim();
  }

  if (!resolvedPatientId) return [];

  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,patient_id,doctor_id,appointment_date,duration_minutes,status,status_reason,superseded_by_appointment_id,consultation_type,is_paid,payment_intent_id,notes,diagnosis_title,diagnosis_notes,practice_id,booked_service_id,booked_service_name,booked_service_price,booked_service_currency,care_subject_type,care_subject_dependant_id,care_subject_name,care_subject_relationship,care_subject_date_of_birth,guardian_user_id,booked_by_user_id,responsible_user_id,responsible_relationship"
    )
    .eq("patient_id", resolvedPatientId)
    .is("superseded_by_appointment_id", null)
    .order("appointment_date", { ascending: false });

  if (error) throw error;

  const nowMs = Date.now();
  const rows = (Array.isArray(data) ? data : []).filter((row) =>
    shouldAppearInPastAppointmentsList(row, nowMs)
  );
  const [context, participationByAppointmentId] = await Promise.all([
    loadDoctorContext(rows.map((row) => row?.doctor_id)),
    loadPatientParticipationByAppointmentIds(rows.map((row) => row?.id)),
  ]);
  return rows.map((row) =>
    mergeSnapshotWithStored(
      buildSnapshotFromRow(
        { ...row, patient_joined_at: participationByAppointmentId.get(String(row?.id || "").trim()) || null },
        context,
        null,
      ),
      readStoredAppointmentSnapshot(row?.id),
    )
  );
}

export async function listMyUpcomingAppointmentSnapshots() {
  const patientId = await getCurrentPatientId();
  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,patient_id,doctor_id,appointment_date,duration_minutes,status,status_reason,superseded_by_appointment_id,consultation_type,is_paid,payment_intent_id,notes,diagnosis_title,diagnosis_notes,practice_id,booked_service_id,booked_service_name,booked_service_price,booked_service_currency,care_subject_type,care_subject_dependant_id,care_subject_name,care_subject_relationship,care_subject_date_of_birth,guardian_user_id,booked_by_user_id,responsible_user_id,responsible_relationship"
    )
    .eq("patient_id", patientId)
    .is("superseded_by_appointment_id", null)
    .in("status", ["confirmed", "in_progress"])
    .order("appointment_date", { ascending: true });

  if (error) throw error;

  const nowMs = Date.now();
  const rows = (Array.isArray(data) ? data : []).filter((row) => isUpcomingAppointment(row, nowMs));
  const context = await loadDoctorContext(rows.map((row) => row?.doctor_id));
  return rows.map((row) =>
    mergeSnapshotWithStored(buildSnapshotFromRow(row, context, null), readStoredAppointmentSnapshot(row?.id))
  );
}

export async function listMyPendingAppointmentSnapshots() {
  const patientId = await getCurrentPatientId();
  const { data, error } = await supabase
    .from("appointments")
    .select(
      "id,patient_id,doctor_id,appointment_date,duration_minutes,status,status_reason,superseded_by_appointment_id,consultation_type,is_paid,payment_intent_id,notes,diagnosis_title,diagnosis_notes,practice_id,booked_service_id,booked_service_name,booked_service_price,booked_service_currency,care_subject_type,care_subject_dependant_id,care_subject_name,care_subject_relationship,care_subject_date_of_birth,guardian_user_id,booked_by_user_id,responsible_user_id,responsible_relationship"
    )
    .eq("patient_id", patientId)
    .is("superseded_by_appointment_id", null)
    .in("status", ["pending", "scheduled"])
    .order("appointment_date", { ascending: true });

  if (error) throw error;

  const nowMs = Date.now();
  const rows = (Array.isArray(data) ? data : []).filter((row) =>
    isPendingApprovalAppointment(row, nowMs)
  );
  const context = await loadDoctorContext(rows.map((row) => row?.doctor_id));
  return rows.map((row) =>
    mergeSnapshotWithStored(buildSnapshotFromRow(row, context, null), readStoredAppointmentSnapshot(row?.id))
  );
}
